/**
 * AffiliatesAdminPage
 *
 * Admin page for creating provisional affiliate accounts and listing all
 * current affiliates (same table as Users, affiliates-only).
 */

import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CopyIcon,
  FormField,
  IconButton,
  Input,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Select,
  Toggle,
} from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import AdminAffiliateUsersPanel from "../components/AdminAffiliateUsersPanel";
import CouponPackPricingEditor from "../components/CouponPackPricingEditor";
import CreateAffiliateCouponsEditor, {
  buildCouponCreatePayload,
  validateCouponDraft,
} from "../components/CreateAffiliateCouponsEditor";
import { useCreateAffiliateUser } from "../hooks/useAdminAffiliates";
import { useCouponDefault } from "../hooks/useAdminCouponDefaults";
import {
  useCouponPackPricingDefault,
  useSaveCouponPackPricingDefault,
} from "../hooks/useAdminCouponPackPricing";
import { buildDefaultCouponCode, buildAlternativeDefaultCouponCode } from "../utils/couponCodeUtils";

// URL-safe username validation aligned with the public register page.
const validateProvisionalUsername = (value) => {
  const urlSafeRegex = /^[a-zA-Z0-9_-]*$/;
  if (!value) return "Username is required.";
  if (!urlSafeRegex.test(value)) return "Username can only contain letters, numbers, hyphens (-), and underscores (_)";
  if (value.length < 5) return "Username must be at least 5 characters long";
  if (value.length > 30) return "Username must be 30 characters or less";
  return "";
};

const noticeStyles = {
  success: "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
  error: "bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
  warning:
    "bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300",
};

const COMMERCIAL_KIND_OPTIONS = [
  {
    value: "affiliate",
    label: "Affiliate",
    description: "Earns commission on referred sales; has a My Payments tab.",
  },
  {
    value: "partner",
    label: "Partner",
    description: "Buys or receives coupon packs; no payments tab.",
  },
];

const kindLabel = (kind) => (kind === "partner" ? "Partner" : "Affiliate");

// Plans that carry paid access; "free" grants nothing, so it is not offered.
const ACCESS_GRANT_PLAN_OPTIONS = [
  { value: "plus", label: "Plus" },
  { value: "pro", label: "Pro" },
];

// A new affiliate or partner gets Plus for six months unless the admin says otherwise.
const DEFAULT_ACCESS_GRANT_PLAN = "plus";
const DEFAULT_ACCESS_GRANT_DAYS = "180";
// Mirrors the backend bounds on access_grant.duration_days.
const ACCESS_GRANT_DAYS_MIN = 1;
const ACCESS_GRANT_DAYS_MAX = 3650;

const buildDefaultAccessGrant = () => ({
  enabled: true,
  plan_name: DEFAULT_ACCESS_GRANT_PLAN,
  duration_days: DEFAULT_ACCESS_GRANT_DAYS,
});

const accessGrantPlanLabel = (planName) =>
  ACCESS_GRANT_PLAN_OPTIONS.find((option) => option.value === planName)?.label || planName;

const AffiliatesAdminPage = () => {
  const createAffiliateMutation = useCreateAffiliateUser();

  const [createForm, setCreateForm] = useState({
    username: "",
    email: "",
    send_welcome_email: false,
    commercial_kind: "affiliate",
  });
  // Free access handed to the new account on creation (admin_manual grant).
  const [accessGrant, setAccessGrant] = useState(buildDefaultAccessGrant);
  const [createCouponDrafts, setCreateCouponDrafts] = useState([]);
  const [expandedCouponKey, setExpandedCouponKey] = useState(null);
  const [createCouponErrors, setCreateCouponErrors] = useState({}); // { [draftKey]: { field: message } }
  // Default template lines (each addable at most once).
  const [defaultCouponLine, setDefaultCouponLine] = useState(null); // { code, touched } | null
  const [defaultAccessLine, setDefaultAccessLine] = useState(null); // { count } | null
  const [defaultLineErrors, setDefaultLineErrors] = useState({}); // { code?, count? }
  const [createErrors, setCreateErrors] = useState({});
  const [createNotice, setCreateNotice] = useState(null); // { type, message }
  const [passwordModal, setPasswordModal] = useState(null); // { username, password, welcomeEmailSent?, welcomeEmailError? } | null
  const [passwordCopyNotice, setPasswordCopyNotice] = useState(null); // { type, message }

  // Global default coupon templates (regular coupon + access-coupon batch).
  const regularDefaultQuery = useCouponDefault("regular");
  const accessDefaultQuery = useCouponDefault("access");
  const regularDefaultConfigured = Boolean(regularDefaultQuery.data?.configured);
  const accessDefaultConfigured = Boolean(accessDefaultQuery.data?.configured);
  const regularDefaultSuffix = regularDefaultQuery.data?.template?.suffix || "";
  // Default coupon codes are derived from the username, so coupons cannot be
  // configured until there is one to derive them from.
  const hasUsername = Boolean(createForm.username.trim());

  // The default coupon code tracks the username live until manually edited.
  const effectiveDefaultCouponCode = defaultCouponLine
    ? defaultCouponLine.touched
      ? defaultCouponLine.code
      : buildDefaultCouponCode(createForm.username, regularDefaultSuffix)
    : "";

  // Global default coupon-pack pricing (volume tiers + override_user_rules boolean).
  // Start closed so the page stays focused on creating/listing accounts.
  const [packPricingOpen, setPackPricingOpen] = useState(false);
  const packPricingQuery = useCouponPackPricingDefault({ enabled: packPricingOpen });
  const savePackPricingMutation = useSaveCouponPackPricingDefault();
  const [overrideUserRules, setOverrideUserRules] = useState(false);
  const [packPricingNotice, setPackPricingNotice] = useState(null); // { type, message }

  useEffect(() => {
    if (packPricingQuery.data) {
      setOverrideUserRules(!!packPricingQuery.data.override_user_rules);
    }
  }, [packPricingQuery.data]);

  const handleSaveDefaultPackPricing = async (tiers) => {
    setPackPricingNotice(null);
    try {
      await savePackPricingMutation.mutateAsync({ tiers, override_user_rules: overrideUserRules });
      setPackPricingNotice({ type: "success", message: "Default pack pricing saved." });
    } catch (err) {
      setPackPricingNotice({ type: "error", message: err?.message || "Failed to save default pack pricing." });
    }
  };

  const handleCreateAffiliate = async (e) => {
    e.preventDefault();
    setCreateNotice(null);

    const errors = {};
    const usernameError = validateProvisionalUsername(createForm.username.trim());
    if (usernameError) errors.username = usernameError;
    const email = createForm.email.trim();
    if (email && !/^\S+@\S+\.\S+$/.test(email)) errors.email = "Enter a valid email address.";
    if (createForm.send_welcome_email && !email) {
      errors.email = "An email address is required to send the welcome email.";
    }

    let accessGrantPayload;
    if (accessGrant.enabled) {
      const durationDays = Number(accessGrant.duration_days);
      if (
        !Number.isInteger(durationDays) ||
        durationDays < ACCESS_GRANT_DAYS_MIN ||
        durationDays > ACCESS_GRANT_DAYS_MAX
      ) {
        errors.access_grant_duration_days = `Enter a whole number of days between ${ACCESS_GRANT_DAYS_MIN} and ${ACCESS_GRANT_DAYS_MAX}.`;
      } else {
        accessGrantPayload = { plan_name: accessGrant.plan_name, duration_days: durationDays };
      }
    }

    const couponErrorsByKey = {};
    const codesSeen = new Set();
    for (const draft of createCouponDrafts) {
      const lineErrors = validateCouponDraft(draft);
      const codeKey = (draft.code || "").trim().toLowerCase();
      if (codeKey) {
        if (codesSeen.has(codeKey)) {
          lineErrors.code = "Duplicate code in this form.";
        }
        codesSeen.add(codeKey);
      }
      if (Object.keys(lineErrors).length > 0) {
        couponErrorsByKey[draft.key] = lineErrors;
      }
    }

    let effectiveDefaultCode = effectiveDefaultCouponCode;
    const defaultErrors = {};
    if (defaultCouponLine) {
      let code = (effectiveDefaultCouponCode || "").trim();
      if (!code) defaultErrors.code = "Code is required.";
      else if (code.length > 64) defaultErrors.code = "Code must be at most 64 characters.";
      else if (codesSeen.has(code.toLowerCase())) {
        // If the default code collides with another code in the form, try adding "2" to the username
        const alt = buildAlternativeDefaultCouponCode(createForm.username, regularDefaultSuffix);
        if (!codesSeen.has(alt.toLowerCase())) {
          effectiveDefaultCode = alt;
        } else {
          defaultErrors.code = "Duplicate code in this form. Edit the code to use a unique value.";
        }
      }
    }
    if (defaultAccessLine) {
      const count = Number(defaultAccessLine.count);
      if (!Number.isInteger(count) || count < 1 || count > 20) {
        defaultErrors.count = "Count must be a whole number between 1 and 20.";
      }
    }

    setCreateErrors(errors);
    setCreateCouponErrors(couponErrorsByKey);
    setDefaultLineErrors(defaultErrors);
    if (
      Object.keys(errors).length > 0 ||
      Object.keys(couponErrorsByKey).length > 0 ||
      Object.keys(defaultErrors).length > 0
    ) {
      const firstBadKey = Object.keys(couponErrorsByKey)[0];
      if (firstBadKey) setExpandedCouponKey(firstBadKey);
      return;
    }

    try {
      const coupons =
        createCouponDrafts.length > 0
          ? createCouponDrafts.map((draft) => buildCouponCreatePayload(draft))
          : undefined;
      const createdUsername = createForm.username.trim();
      const createdKindLabel = kindLabel(createForm.commercial_kind);
      const result = await createAffiliateMutation.mutateAsync({
        username: createdUsername,
        email: email || undefined,
        send_welcome_email: createForm.send_welcome_email || undefined,
        commercial_kind: createForm.commercial_kind,
        coupons,
        default_coupon: defaultCouponLine ? { code: effectiveDefaultCode.trim() } : undefined,
        default_access: defaultAccessLine ? { count: Number(defaultAccessLine.count) } : undefined,
        access_grant: accessGrantPayload,
      });
      setCreateForm({ username: "", email: "", send_welcome_email: false, commercial_kind: "affiliate" });
      setAccessGrant(buildDefaultAccessGrant());
      setCreateCouponDrafts([]);
      setExpandedCouponKey(null);
      setCreateCouponErrors({});
      setCreateErrors({});
      setDefaultCouponLine(null);
      setDefaultAccessLine(null);
      setDefaultLineErrors({});

      const createdCount = Array.isArray(result?.coupons) ? result.coupons.length : 0;
      const syncWarnings = Array.isArray(result?.coupon_sync_warnings)
        ? result.coupon_sync_warnings.filter(Boolean)
        : [];
      let message = result?.welcome_email_sent
        ? `${createdKindLabel} account created and credentials emailed.`
        : `${createdKindLabel} account created.`;
      if (createdCount > 0) {
        message += ` ${createdCount} coupon${createdCount === 1 ? "" : "s"} linked.`;
      }
      const grantedAccess = result?.access_grant;
      if (grantedAccess) {
        const grantedDays = grantedAccess.duration_days;
        message += ` ${accessGrantPlanLabel(grantedAccess.plan_name)} access granted for ${grantedDays} day${
          grantedDays === 1 ? "" : "s"
        }.`;
      }
      if (result?.welcome_email_error) {
        setCreateNotice({
          type: "warning",
          message: `${createdKindLabel} account created, but the welcome email failed: ${result.welcome_email_error}`,
        });
      } else if (syncWarnings.length > 0) {
        setCreateNotice({
          type: "warning",
          message: `${message} Stripe sync warning: ${syncWarnings.join("; ")}`,
        });
      } else {
        setCreateNotice({ type: "success", message });
      }

      if (result?.initial_password) {
        setPasswordCopyNotice(null);
        setPasswordModal({
          username: result?.user?.username || createdUsername,
          password: result.initial_password,
          welcomeEmailSent: Boolean(result?.welcome_email_sent),
          welcomeEmailError: result?.welcome_email_error || null,
        });
      }
    } catch (err) {
      setCreateNotice({ type: "error", message: err?.message || "Failed to create account." });
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className={`${typography.h2} ${textColors.primary}`}>Affiliates</h1>
          <p className={`${typography.body} ${textColors.secondary} mt-1`}>
            Create provisional affiliate and partner accounts and manage everyone with portal access.
          </p>
        </div>

        <Card variant="glass">
          <CardHeader
            title="Create account"
            subtitle="Create a provisional affiliate or partner account. They complete onboarding (email + new password) on first access."
          />
          <CardBody>
            <form onSubmit={handleCreateAffiliate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Username" htmlFor="create-affiliate-username" required>
                  <Input
                    id="create-affiliate-username"
                    value={createForm.username}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCreateForm((p) => ({ ...p, username: value }));
                      setCreateErrors((p) => ({ ...p, username: undefined }));
                    }}
                    error={createErrors.username}
                    placeholder="e.g. affiliate-name"
                  />
                </FormField>

                <FormField
                  label="Email"
                  htmlFor="create-affiliate-email"
                  optional
                  helperText="Optional. If omitted, the affiliate will set their email on first access."
                >
                  <Input
                    id="create-affiliate-email"
                    type="email"
                    value={createForm.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCreateForm((p) => ({ ...p, email: value }));
                      setCreateErrors((p) => ({ ...p, email: undefined }));
                    }}
                    error={createErrors.email}
                    placeholder="affiliate@example.com"
                  />
                </FormField>
              </div>

              <div>
                <span className={`block mb-2 ${typography.label} ${textColors.secondary}`}>Account kind</span>
                <div
                  role="radiogroup"
                  aria-label="Account kind"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  {COMMERCIAL_KIND_OPTIONS.map((option) => {
                    const isSelected = createForm.commercial_kind === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        disabled={createAffiliateMutation.isPending}
                        onClick={() => setCreateForm((p) => ({ ...p, commercial_kind: option.value }))}
                        className={`text-left p-3 border rounded-2xl transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed ${
                          isSelected
                            ? "border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-200 dark:ring-blue-900/50"
                            : `${borders.glass} ${surfaces.glass} ${surfaces.hover}`
                        }`}
                      >
                        <span className={`block ${typography.bodySmall} font-medium ${textColors.primary}`}>
                          {option.label}
                        </span>
                        <span className={`block mt-0.5 ${typography.caption} ${textColors.secondary}`}>
                          {option.description}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Toggle
                id="create-affiliate-send-welcome-email"
                checked={createForm.send_welcome_email}
                onChange={(e) => {
                  const value = Boolean(e?.target?.checked);
                  setCreateForm((p) => ({ ...p, send_welcome_email: value }));
                  setCreateErrors((p) => ({ ...p, email: undefined }));
                }}
                label="Send welcome email with credentials"
                description="Emails the affiliate their username and temporary password. Requires an email address."
                disabled={createAffiliateMutation.isPending}
              />

              <div className="space-y-4">
                <Toggle
                  id="create-affiliate-grant-access"
                  checked={accessGrant.enabled}
                  onChange={(e) => {
                    const value = Boolean(e?.target?.checked);
                    setAccessGrant((p) => ({ ...p, enabled: value }));
                    setCreateErrors((p) => ({ ...p, access_grant_duration_days: undefined }));
                  }}
                  label="Grant free access"
                  description="Gives the new account a paid plan for a fixed number of days — no payment and no renewal. On by default."
                  disabled={createAffiliateMutation.isPending}
                />

                {accessGrant.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Access plan" htmlFor="create-affiliate-access-plan">
                      <Select
                        id="create-affiliate-access-plan"
                        value={accessGrant.plan_name}
                        onChange={(e) => {
                          const value = e.target.value;
                          setAccessGrant((p) => ({ ...p, plan_name: value }));
                        }}
                        options={ACCESS_GRANT_PLAN_OPTIONS}
                        placeholder=""
                        disabled={createAffiliateMutation.isPending}
                      />
                    </FormField>

                    <FormField
                      label="Access duration (days)"
                      htmlFor="create-affiliate-access-days"
                      helperText="180 days is about six months."
                    >
                      <Input
                        id="create-affiliate-access-days"
                        type="number"
                        min={ACCESS_GRANT_DAYS_MIN}
                        max={ACCESS_GRANT_DAYS_MAX}
                        value={accessGrant.duration_days}
                        onChange={(e) => {
                          const value = e.target.value;
                          setAccessGrant((p) => ({ ...p, duration_days: value }));
                          setCreateErrors((p) => ({ ...p, access_grant_duration_days: undefined }));
                        }}
                        error={createErrors.access_grant_duration_days}
                        disabled={createAffiliateMutation.isPending}
                      />
                    </FormField>
                  </div>
                )}
              </div>

              <CreateAffiliateCouponsEditor
                drafts={createCouponDrafts}
                expandedKey={expandedCouponKey}
                errorsByKey={createCouponErrors}
                disabled={createAffiliateMutation.isPending || !hasUsername}
                disabledReason={
                  hasUsername ? "" : "Enter a username first — coupon codes are built from it."
                }
                onChange={(updater) => {
                  setCreateCouponDrafts(updater);
                  setCreateCouponErrors({});
                }}
                onExpandedKeyChange={setExpandedCouponKey}
                defaults={{
                  regular: {
                    configured: regularDefaultConfigured,
                    loading: regularDefaultQuery.isLoading,
                    added: !!defaultCouponLine,
                    code: effectiveDefaultCouponCode,
                    error: defaultLineErrors.code,
                    onAdd: () => {
                      setDefaultCouponLine({ code: "", touched: false });
                      setDefaultLineErrors((p) => ({ ...p, code: undefined }));
                    },
                    onRemove: () => {
                      setDefaultCouponLine(null);
                      setDefaultLineErrors((p) => ({ ...p, code: undefined }));
                    },
                    onCodeChange: (value) => {
                      setDefaultCouponLine({ code: value.slice(0, 64), touched: true });
                      setDefaultLineErrors((p) => ({ ...p, code: undefined }));
                    },
                  },
                  access: {
                    configured: accessDefaultConfigured,
                    loading: accessDefaultQuery.isLoading,
                    added: !!defaultAccessLine,
                    count: defaultAccessLine?.count ?? "",
                    error: defaultLineErrors.count,
                    onAdd: () => {
                      setDefaultAccessLine({
                        count: String(accessDefaultQuery.data?.template?.default_count ?? 1),
                      });
                      setDefaultLineErrors((p) => ({ ...p, count: undefined }));
                    },
                    onRemove: () => {
                      setDefaultAccessLine(null);
                      setDefaultLineErrors((p) => ({ ...p, count: undefined }));
                    },
                    onCountChange: (value) => {
                      setDefaultAccessLine({ count: value });
                      setDefaultLineErrors((p) => ({ ...p, count: undefined }));
                    },
                  },
                }}
              />

              {createNotice && (
                <div className={`rounded-2xl px-4 py-3 ${noticeStyles[createNotice.type]}`}>
                  <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{createNotice.message}</p>
                </div>
              )}

              <div className="flex items-center justify-end">
                <PrimaryButton type="submit" loading={createAffiliateMutation.isPending}>
                  Create account
                </PrimaryButton>
              </div>
            </form>
          </CardBody>
        </Card>

        <Card variant="glass">
          <CardHeader
            title="Default pack pricing"
            subtitle={
              packPricingOpen
                ? "Volume-discount tiers that price the self-serve Buy Coupons packs. For each order, the tier with the highest minimum quantity at or below the order size applies."
                : "Volume discounts for Buy Coupons packs."
            }
            actions={
              <SecondaryButton
                type="button"
                onClick={() => setPackPricingOpen((open) => !open)}
                aria-expanded={packPricingOpen}
                aria-controls="default-pack-pricing-panel"
              >
                {packPricingOpen ? "Hide pricing" : "Show pricing"}
              </SecondaryButton>
            }
          />
          {packPricingOpen && (
            <CardBody id="default-pack-pricing-panel" className="space-y-4">
              {packPricingQuery.isLoading ? (
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>Loading pack pricing…</p>
              ) : packPricingQuery.error ? (
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                  Failed to load pack pricing: {packPricingQuery.error?.message || "Unknown error"}
                </p>
              ) : (
                <>
                  <Toggle
                    id="pack-pricing-override-user-rules"
                    checked={overrideUserRules}
                    onChange={(e) => setOverrideUserRules(Boolean(e?.target?.checked))}
                    label="Override user-specific pricing"
                    description="When on, this default table is used for everyone, even users with custom pricing."
                    disabled={savePackPricingMutation.isPending}
                  />

                  <CouponPackPricingEditor
                    tiers={packPricingQuery.data?.tiers || []}
                    saving={savePackPricingMutation.isPending}
                    saveLabel="Save"
                    idPrefix="default-pack-pricing"
                    onSave={handleSaveDefaultPackPricing}
                    onCancel={() => {
                      setOverrideUserRules(!!packPricingQuery.data?.override_user_rules);
                      setPackPricingNotice(null);
                    }}
                  />
                </>
              )}

              {packPricingNotice && (
                <div className={`rounded-2xl px-4 py-3 ${noticeStyles[packPricingNotice.type]}`}>
                  <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{packPricingNotice.message}</p>
                </div>
              )}
            </CardBody>
          )}
        </Card>

        <AdminAffiliateUsersPanel forceOnlyAffiliates />

        <Modal
          isOpen={!!passwordModal}
          onClose={() => {
            setPasswordModal(null);
            setPasswordCopyNotice(null);
          }}
          title="New password generated"
          size="md"
          footer={
            <SecondaryButton
              type="button"
              onClick={() => {
                setPasswordModal(null);
                setPasswordCopyNotice(null);
              }}
            >
              Done
            </SecondaryButton>
          }
        >
          {passwordModal && (
            <div className="space-y-4">
              {passwordModal.welcomeEmailError ? (
                <div className={`rounded-2xl px-4 py-3 ${noticeStyles.warning}`}>
                  <p className={`${typography.bodySmall} whitespace-pre-wrap`}>
                    The welcome email could not be sent ({passwordModal.welcomeEmailError}). Share the password below manually.
                  </p>
                </div>
              ) : passwordModal.welcomeEmailSent ? (
                <div className={`rounded-2xl px-4 py-3 ${noticeStyles.success}`}>
                  <p className={typography.bodySmall}>
                    A welcome email with these credentials was sent to the affiliate.
                  </p>
                </div>
              ) : null}

              <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                Initial password for <span className={`${textColors.primary} font-medium`}>{passwordModal.username}</span>:
              </p>

              <div className="flex items-center gap-2 min-w-0">
                <code
                  className={`flex-1 min-w-0 px-3 py-2 rounded-2xl ${surfaces.glass} ${borders.glass} border ${typography.bodySmall} ${textColors.primary} break-all`}
                  aria-label="Initial password"
                >
                  {passwordModal.password}
                </code>
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label="Copy initial password"
                  title="Copy"
                  icon={<CopyIcon size="sm" />}
                  onClick={async () => {
                    setPasswordCopyNotice(null);
                    try {
                      if (navigator?.clipboard?.writeText) {
                        await navigator.clipboard.writeText(passwordModal.password);
                        setPasswordCopyNotice({ type: "success", message: "Copied password." });
                      } else {
                        throw new Error("Clipboard not available");
                      }
                    } catch (_e) {
                      setPasswordCopyNotice({
                        type: "error",
                        message: "Could not copy automatically. Please copy the password manually.",
                      });
                    }
                  }}
                />
              </div>

              {passwordCopyNotice && (
                <div className={`rounded-2xl px-3 py-2 ${noticeStyles[passwordCopyNotice.type]}`}>
                  <p className={`${typography.bodySmall}`}>{passwordCopyNotice.message}</p>
                </div>
              )}

              <div className="rounded-2xl px-4 py-3 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300">
                <p className={`${typography.bodySmall}`}>
                  This password will not be shown again. Share it with the affiliate now — if it is lost, you can only
                  regenerate a new one.
                </p>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default AffiliatesAdminPage;
